import { useEffect, useState } from 'react';
import { History, Play, X } from 'lucide-react';
import { reciterById } from '@/data/reciters';
import { chapter } from '@/features/quran/api';
import { haptic } from '@/components/ui';
import { usePlayer } from './audioEngine';
import { useRuqyah } from '@/features/adhkar/ruqyahPlayer';
import { useCanHear } from './access';
import { latestResumable, resumeFrom, useResume, type ResumePoint } from './resume';

const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
};
const what = (p: ResumePoint) => (p.juz && !p.clip ? `Juz ${p.juz}` : chapter(p.surah).n) + (p.verseKey ? ` · ${p.verseKey}` : '');

const TOAST_MS = 10_000;

/* Right after Stop (or starting something else over a track in progress),
   offer to undo it. Sits above the tab bar for ten seconds. */
export function StoppedToast() {
  const latest = useResume((s) => s.points[0]);
  const status = usePlayer((s) => s.status);
  // Starting ruqyah stops the Qurʾān player on purpose — no "Stopped" toast over the ruqyah bar.
  const ruqyahOn = useRuqyah((s) => s.status !== 'idle');
  const [shown, setShown] = useState<ResumePoint | null>(null);

  useEffect(() => {
    if (!latest || (latest.reason !== 'stop' && latest.reason !== 'replaced') || Date.now() - latest.at > 2_000) return;
    setShown(latest);
    const t = setTimeout(() => setShown(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [latest]);
  // A Stop toast is pointless once something is playing again.
  useEffect(() => { if (shown?.reason === 'stop' && status === 'playing') setShown(null); }, [status, shown]);

  if (!shown || ruqyahOn) return null;
  return (
    <div role="status" className="glass fixed inset-x-3 z-50 mx-auto flex max-w-[560px] items-center gap-3 rounded-[20px] py-2 pl-4 pr-2"
      style={{ bottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 14px)' }}>
      <History size={18} className="shrink-0 text-gold" aria-hidden />
      <div className="min-w-0 flex-1 text-[14px] leading-tight">
        <div className="truncate font-semibold">{shown.reason === 'stop' ? 'Stopped' : 'Left'} {what(shown)}</div>
        <div className="truncate text-[12px] text-muted">{reciterById(shown.reciterId).name} · at {clock(shown.positionMs)}</div>
      </div>
      <button type="button" className="press rounded-full bg-accent px-3.5 py-2 text-[14px] font-semibold text-accent-ink"
        onClick={() => { haptic(); setShown(null); void resumeFrom(shown); }}>{shown.reason === 'stop' ? 'Resume' : 'Go back'}</button>
      <button type="button" aria-label="Dismiss" className="press grid h-8 w-8 place-items-center rounded-full text-muted" onClick={() => setShown(null)}><X size={16} /></button>
    </div>
  );
}

/** "Continue listening" — the newest stopped point that isn't playing now. */
export function ContinueListening({ className = '' }: { className?: string }) {
  const all = useResume((s) => s.points);
  const dismiss = useResume((s) => s.dismiss);
  const player = usePlayer();
  const can = useCanHear();
  const points = all.filter((x) => can(reciterById(x.reciterId)));
  const p = latestResumable(points, player);
  if (!p) return null;
  const pct = p.durationMs ? Math.min(1, p.positionMs / p.durationMs) : 0;
  return (
    <div className={`flex items-center gap-3 rounded-[20px] bg-surface p-3 shadow-[inset_0_0_0_1px_var(--card-edge)] ${className}`}>
      <button type="button" aria-label={`Continue ${what(p)}`} className="press grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-accent-ink"
        onClick={() => { haptic(); void resumeFrom(p); }}><Play size={20} /></button>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Continue listening</div>
        <div className="truncate text-[15px] font-semibold">{what(p)}</div>
        <div className="truncate text-[12px] text-muted">{reciterById(p.reciterId).name} · {clock(p.positionMs)}{p.durationMs ? ` of ${clock(p.durationMs)}` : ''}</div>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-fill"><div className="h-full rounded-full bg-accent" style={{ width: `${pct * 100}%` }} /></div>
      </div>
      <button type="button" aria-label="Forget this" className="press grid h-8 w-8 shrink-0 place-items-center rounded-full text-faint" onClick={() => dismiss(p.key)}><X size={16} /></button>
    </div>
  );
}
